import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import * as Cesium from 'cesium';
import { elevationService } from '../services/elevationService';
import { DEFAULT_MEASUREMENT_COLOR } from '../components/MeasureTool/types';
import { fetchLineElevationProfile } from './common/lineElevationSampling';
import { isPointInPolygon, computeCopyOffsetLatLng, boundingExtentMeters } from '../utils/geometryUtils';
import { useVisibilityControl } from './useVisibilityControl';
import { useLocalStoragePersistence } from './useLocalStoragePersistence';
import { useEventSubscribe } from '../events/hooks';
import { createMeasurementPoint, createMeasurementLine, createMeasurementLabel, makePolylineWidthProperty } from '../utils/cesiumEntityFactory';
import { createCircleCenterPoint, createCircleEntity as createCircleShapeEntity, createCircleLabel, createCircleOutline, computeCircleRing, DEFAULT_CIRCLE_FILL_OPACITY, } from '../utils/measureCircleEntity';
import { formatDistance, formatAreaFromSqMeters, formatSquaredDistanceArea } from '../utils/units';
import { usePickTerrainPosition } from './common/useCesiumInteraction';
import { useCustomDataSource } from './common/useCustomDataSource';
import { disableCameraControls, enableCameraControls, groundedWindowPosition } from '../utils/cesiumHelpers';
import { STORAGE_KEY, serializeMeasurementsForStorage, deserializeStoredMeasurements } from './measure/measureSerialization';
// Geodesic compass bearing in degrees [0, 360) from start → end.
const computeBearingDeg = (start, end) => {
    const startCart = Cesium.Cartographic.fromCartesian(start);
    const endCart = Cesium.Cartographic.fromCartesian(end);
    const geodesic = new Cesium.EllipsoidGeodesic(startCart, endCart);
    return ((Cesium.Math.toDegrees(geodesic.startHeading) % 360) + 360) % 360;
};
// Cardinal-points helper for the circle resize handles. Order is fixed
// at N, E, S, W so callers can index the returned array predictably.
// Equirectangular offset — matches computeCircleRing for visual parity.
const EARTH_RADIUS_M = 6378137;
const computeCardinalPoints = (center, radiusMeters) => {
    const c = Cesium.Cartographic.fromCartesian(center);
    const dLat = radiusMeters / EARTH_RADIUS_M;
    const dLng = radiusMeters / (EARTH_RADIUS_M * Math.cos(c.latitude));
    return [
        Cesium.Cartesian3.fromRadians(c.longitude, c.latitude + dLat),
        Cesium.Cartesian3.fromRadians(c.longitude + dLng, c.latitude),
        Cesium.Cartesian3.fromRadians(c.longitude, c.latitude - dLat),
        Cesium.Cartesian3.fromRadians(c.longitude - dLng, c.latitude),
    ];
};
export const useMeasure = ({ viewer, isActive, projectId, constraintPolygon, defaultDistanceUnit, defaultAreaUnit: propDefaultAreaUnit, readyLayers, }) => {
    const [measurements, setMeasurements] = useState(new Map());
    const [circles, setCircles] = useState(new Map());
    const [pendingMeasurement, setPendingMeasurement] = useState(null);
    const [pendingCircle, setPendingCircle] = useState(null);
    const [selectedMeasurement, setSelectedMeasurement] = useState(null);
    const [selectedCircle, setSelectedCircle] = useState(null);
    const [hoveredMeasurement, setHoveredMeasurement] = useState(null);
    const [drawingHUD, setDrawingHUD] = useState(null);
    const [dragging, setDragging] = useState(null);
    const [measureMode, setMeasureMode] = useState('line');
    const pendingCircleRadiusRef = useRef(0);
    const [poppedOutProfiles, setPoppedOutProfiles] = useState([]);
    const [itemOpacities, setItemOpacities] = useState(new Map());
    // Distance + area units always derive from the global preference
    // passed in by the parent. Falls back to a localStorage read at first
    // render for the legacy path when the parent doesn't pass them yet.
    const readLegacyDistance = () => {
        try {
            const prefs = JSON.parse(localStorage.getItem('landscope_unit_preferences') || '{}');
            if (prefs.distanceUnit === 'm' || prefs.distanceUnit === 'ft')
                return prefs.distanceUnit;
        }
        catch ( /* ignore */_a) { /* ignore */ }
        return 'ft';
    };
    const readLegacyArea = () => {
        try {
            const prefs = JSON.parse(localStorage.getItem('landscope_unit_preferences') || '{}');
            if (prefs.areaUnit === 'sqm' || prefs.areaUnit === 'sqft' || prefs.areaUnit === 'acres' || prefs.areaUnit === 'hectares')
                return prefs.areaUnit;
        }
        catch ( /* ignore */_a) { /* ignore */ }
        return 'acres';
    };
    const defaultUnit = defaultDistanceUnit !== null && defaultDistanceUnit !== void 0 ? defaultDistanceUnit : readLegacyDistance();
    const defaultAreaUnit = propDefaultAreaUnit !== null && propDefaultAreaUnit !== void 0 ? propDefaultAreaUnit : readLegacyArea();
    // updateMeasurement*Unit handlers still need to mutate the per-measurement
    // displayUnit but the GLOBAL default is owned by the parent's
    // useUnitPreferences hook. These no-op setters keep call sites compiling
    // — when they fire (e.g. the dialog UnitPill flips a measurement's unit)
    // they ALSO call unitPreferences.setDistanceUnit upstream, which flows
    // back into defaultUnit on the next render.
    const setDefaultUnit = useCallback((_) => { }, []);
    const setDefaultAreaUnit = useCallback((_) => { }, []);
    const circleCounterRef = useRef(1);
    const dataSourceRef = useCustomDataSource(viewer, 'measurements');
    const elevationDebounceRef = useRef(null);
    const profileFetchGenRef = useRef(0);
    const measurementCounterRef = useRef(1);
    // Live refs for the undo/redo snapshot machinery so captureConfigs reads the
    // current geometry without re-creating callbacks on every edit.
    const measurementsRef = useRef(measurements);
    measurementsRef.current = measurements;
    const circlesRef = useRef(circles);
    circlesRef.current = circles;
    const selectedMeasurementRef = useRef(selectedMeasurement);
    selectedMeasurementRef.current = selectedMeasurement;
    const selectedCircleRef = useRef(selectedCircle);
    selectedCircleRef.current = selectedCircle;
    // Undo / redo of GEOMETRY edits (endpoint/whole-line drag, circle move/resize,
    // create, delete, duplicate). Each stack entry is a full geometry snapshot
    // (MeasurementConfig[] covering both lines and circles); the pre-change state
    // is pushed at each edit site, and undo/redo rebuild via restoreFromConfig.
    // Geometry only — async profiles and colour changes don't record. Mirrors the
    // polygon/path tools 1:1.
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const UNDO_LIMIT = 50;
    const captureConfigs = useCallback(() => {
        const out = [];
        measurementsRef.current.forEach((m) => {
            const startCart = Cesium.Cartographic.fromCartesian(m.startPoint);
            const endCart = Cesium.Cartographic.fromCartesian(m.endPoint);
            out.push({
                kind: 'line',
                id: m.id,
                name: m.name,
                startLat: Cesium.Math.toDegrees(startCart.latitude),
                startLng: Cesium.Math.toDegrees(startCart.longitude),
                endLat: Cesium.Math.toDegrees(endCart.latitude),
                endLng: Cesium.Math.toDegrees(endCart.longitude),
                color: m.color,
                unit: m.displayUnit,
                projectId: m.projectId,
                lineWidth: m.lineWidth,
                colorBy: m.colorBy,
                showLabel: m.showLabel,
            });
        });
        circlesRef.current.forEach((c) => {
            const centerCart = Cesium.Cartographic.fromCartesian(c.center);
            out.push({
                kind: 'circle',
                id: c.id,
                name: c.name,
                centerLat: Cesium.Math.toDegrees(centerCart.latitude),
                centerLng: Cesium.Math.toDegrees(centerCart.longitude),
                radiusMeters: c.radiusMeters,
                color: c.color,
                outlineColor: c.outlineColor,
                fillOpacity: c.fillOpacity,
                unit: c.displayUnit,
                areaUnit: c.areaUnit,
                projectId: c.projectId,
                lineWidth: c.lineWidth,
                showLabel: c.showLabel,
            });
        });
        return out;
    }, []);
    const pushUndoSnapshot = useCallback(() => {
        undoStackRef.current.push(captureConfigs());
        if (undoStackRef.current.length > UNDO_LIMIT)
            undoStackRef.current.shift();
        redoStackRef.current = [];
    }, [captureConfigs]);
    // When a previously-not-ready analytical layer finishes processing, refetch
    // the profiles of already-drawn measurements so the now-available layer fills
    // in (its colour-by option un-greys and actually paints).
    const readyLayersSig = (readyLayers !== null && readyLayers !== void 0 ? readyLayers : []).slice().sort().join(',');
    const prevReadyLayersSigRef = useRef(readyLayersSig);
    useEffect(() => {
        const prev = prevReadyLayersSigRef.current;
        prevReadyLayersSigRef.current = readyLayersSig;
        if (prev === readyLayersSig || !projectId || measurements.size === 0)
            return;
        const prevSet = new Set(prev ? prev.split(',') : []);
        const grew = readyLayersSig.split(',').filter(Boolean).some(l => !prevSet.has(l));
        if (!grew)
            return;
        let cancelled = false;
        (async () => {
            for (const [id, m] of Array.from(measurements.entries())) {
                if (!m.startPoint || !m.endPoint)
                    continue;
                const gen = ++profileFetchGenRef.current;
                const profile = await fetchLineElevationProfile(projectId, m.startPoint, m.endPoint, readyLayers);
                if (cancelled || gen !== profileFetchGenRef.current)
                    continue;
                setMeasurements(current => {
                    const newMap = new Map(current);
                    const existing = newMap.get(id);
                    if (existing)
                        newMap.set(id, { ...existing, elevationProfile: profile || null });
                    return newMap;
                });
            }
        })();
        return () => { cancelled = true; };
    }, [readyLayersSig, projectId, measurements, readyLayers]);
    const getMeasurementEntities = useCallback((m) => {
        // Shape first (line / circle fill): useVisibilityControl uses the first defined
        // entity as the visibility proxy, and the endpoint / center handles are hidden
        // when the measurement isn't selected. Reading a hidden handle as "invisible"
        // would deselect the measurement and flicker its dialog.
        if (m.kind === 'line') {
            return [m.lineEntity, m.startEntity, m.endEntity, m.labelEntity];
        }
        return [m.circleEntity, m.outlineEntity, m.centerEntity, m.labelEntity];
    }, []);
    // Unified view of all measurements for visibility/opacity facades.
    const allMeasurements = useMemo(() => {
        const merged = new Map();
        measurements.forEach((m, id) => merged.set(id, m));
        circles.forEach((c, id) => merged.set(id, c));
        return merged;
    }, [measurements, circles]);
    const { isVisible, hideAll, showAll, toggleItemVisibility, isItemVisible } = useVisibilityControl(allMeasurements, getMeasurementEntities);
    const prevProjectIdRef = useRef(null);
    useEffect(() => {
        if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== projectId) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.removeAll();
            }
            setMeasurements(new Map());
            setCircles(new Map());
            setPendingMeasurement(null);
            setPendingCircle(null);
            setSelectedMeasurement(null);
            setSelectedCircle(null);
            setHoveredMeasurement(null);
            setDragging(null);
            undoStackRef.current = [];
            redoStackRef.current = [];
        }
        prevProjectIdRef.current = projectId;
    }, [projectId]);
    const createStartPointEntity = useCallback((position, color) => {
        if (!dataSourceRef.current)
            return null;
        return createMeasurementPoint(dataSourceRef.current, position, color, true);
    }, []);
    const createEndPointEntity = useCallback((position, color) => {
        if (!dataSourceRef.current)
            return null;
        return createMeasurementPoint(dataSourceRef.current, position, color, false);
    }, []);
    const createLineEntity = useCallback((start, end, color, width) => {
        if (!dataSourceRef.current)
            return null;
        return createMeasurementLine(dataSourceRef.current, [start, end], color, 1.0, width, viewer !== null && viewer !== void 0 ? viewer : undefined);
    }, [viewer]);
    // Build the on-map label text for a line. Includes bearing as a
    // compass heading next to the distance — both are useful at-a-glance
    // values when planning oriented features (windbreaks, fence runs, etc).
    const formatLineLabel = (distance, unit, bearingDeg) => `${formatDistance(distance, unit)} · ${bearingDeg.toFixed(1)}°`;
    // Text shown on a circle's on-map label when Show Label is on. Hoisted
    // alongside formatLineLabel so the drag move handler (which updates the
    // label live during resize) can reference it without a TDZ warning.
    const formatCircleLabel = (radiusMeters, distanceUnit, areaUnit) => {
        const area = Math.PI * radiusMeters * radiusMeters;
        return `Radius ${formatDistance(radiusMeters, distanceUnit)} · Area ${formatAreaFromSqMeters(area, areaUnit)}`;
    };
    const createLabelEntity = useCallback((start, end, distance, unit, _color, bearingDeg) => {
        if (!dataSourceRef.current)
            return null;
        const midpoint = Cesium.Cartesian3.midpoint(start, end, new Cesium.Cartesian3());
        return createMeasurementLabel(dataSourceRef.current, midpoint, formatLineLabel(distance, unit, bearingDeg));
    }, []);
    // Circle entity factories
    const createCenterPointEntity = useCallback((position, color) => {
        if (!dataSourceRef.current)
            return null;
        return createCircleCenterPoint(dataSourceRef.current, position, color);
    }, []);
    // Mid-draw ghost: ellipse-only (no separate outline polyline) for
    // simplicity. The radius tracks the cursor via CallbackProperty;
    // re-computing a polyline ring every frame would be wasteful.
    const createPendingCircleEntity = useCallback((center, color) => {
        if (!dataSourceRef.current)
            return null;
        const radiusCallback = new Cesium.CallbackProperty(() => pendingCircleRadiusRef.current || 0.01, false);
        return createCircleShapeEntity(dataSourceRef.current, center, radiusCallback, color, 1.0);
    }, []);
    // Committed circle fill (no outline — outline is a separate polyline).
    const createCommittedCircleEntity = useCallback((center, radiusMeters, color, fillOpacity) => {
        if (!dataSourceRef.current)
            return null;
        return createCircleShapeEntity(dataSourceRef.current, center, radiusMeters, color, fillOpacity !== null && fillOpacity !== void 0 ? fillOpacity : DEFAULT_CIRCLE_FILL_OPACITY);
    }, []);
    // Polyline boundary — works around Cesium ellipse.outlineWidth being
    // browser-clamped to 1px. Polyline width is honored at any value.
    const createCircleOutlineEntity = useCallback((center, radiusMeters, color, width) => {
        if (!dataSourceRef.current)
            return null;
        return createCircleOutline(dataSourceRef.current, center, radiusMeters, color, width !== null && width !== void 0 ? width : 3, viewer !== null && viewer !== void 0 ? viewer : undefined);
    }, [viewer]);
    const pickTerrainPosition = usePickTerrainPosition(viewer);
    // Update measurement entity visuals during drag (no state update — avoids re-render churn)
    const updateDraggedEntities = useCallback((id, newStart, newEnd) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return;
        if (measurement.lineEntity.polyline) {
            measurement.lineEntity.polyline.positions = new Cesium.ConstantProperty([newStart, newEnd]);
        }
        const newDistance = Cesium.Cartesian3.distance(newStart, newEnd);
        const newBearing = computeBearingDeg(newStart, newEnd);
        const midpoint = Cesium.Cartesian3.midpoint(newStart, newEnd, new Cesium.Cartesian3());
        if (measurement.labelEntity.position) {
            measurement.labelEntity.position = midpoint;
        }
        if (measurement.labelEntity.label) {
            measurement.labelEntity.label.text = new Cesium.ConstantProperty(formatLineLabel(newDistance, measurement.displayUnit, newBearing));
        }
    }, [measurements]);
    // Handle mouse down to start dragging — supports three modes:
    //   1. Line endpoint drag (clicked on a start/end point entity)
    //   2. Line whole-line drag (clicked on the line body; preserves
    //      bearing + length while translating)
    //   3. Circle center drag (clicked on the center handle of the
    //      currently-selected circle; preserves radius)
    const handleMapMouseDown = useCallback((event) => {
        var _a;
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (!(pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id))
            return false;
        const entity = pickedObject.id;
        // Endpoint drag (original behavior).
        for (const [id, measurement] of measurements.entries()) {
            if (entity === measurement.startEntity || entity === measurement.endEntity) {
                const pointType = entity === measurement.startEntity ? 'start' : 'end';
                setDragging({ kind: 'line-endpoint', measurementId: id, pointType });
                disableCameraControls(viewer);
                return true;
            }
        }
        // Whole-line drag: clicked on the line body itself.
        for (const [id, measurement] of measurements.entries()) {
            if (entity === measurement.lineEntity) {
                const cursor = pickTerrainPosition(event.position);
                if (!cursor)
                    return false;
                setDragging({
                    kind: 'line-translate',
                    measurementId: id,
                    initialStart: measurement.startPoint,
                    initialEnd: measurement.endPoint,
                    initialCursor: cursor,
                });
                disableCameraControls(viewer);
                return true;
            }
        }
        // Circle center / resize drag — only when the circle is currently
        // selected (handles are only present then; clicking the fill of a
        // non-selected circle still selects the circle, no drag).
        if (selectedCircle) {
            const circle = circles.get(selectedCircle.measurementId);
            if (circle) {
                if (entity === circle.centerEntity) {
                    const cursor = pickTerrainPosition(event.position);
                    if (!cursor)
                        return false;
                    setDragging({
                        kind: 'circle-center',
                        circleId: circle.id,
                        initialCenter: circle.center,
                        initialCursor: cursor,
                    });
                    disableCameraControls(viewer);
                    return true;
                }
                if ((_a = circle.resizeHandles) === null || _a === void 0 ? void 0 : _a.some(h => entity === h)) {
                    setDragging({ kind: 'circle-resize', circleId: circle.id });
                    disableCameraControls(viewer);
                    return true;
                }
            }
        }
        return false;
    }, [viewer, measurements, circles, selectedCircle, pickTerrainPosition]);
    // Check if clicking on an existing measurement (can be called regardless of isActive)
    const checkExistingMeasurementClick = useCallback((event) => {
        var _a;
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            const entity = pickedObject.id;
            for (const [id, measurement] of measurements.entries()) {
                // Clicking on a point or line or label shows options dialog
                if (entity === measurement.startEntity || entity === measurement.endEntity ||
                    entity === measurement.lineEntity || entity === measurement.labelEntity) {
                    setSelectedMeasurement({
                        measurementId: id,
                        screenPosition: { x: event.position.x, y: event.position.y }
                    });
                    return true;
                }
            }
            for (const [id, circle] of circles.entries()) {
                if (entity === circle.circleEntity || entity === circle.outlineEntity
                    || entity === circle.centerEntity || entity === circle.labelEntity
                    || ((_a = circle.resizeHandles) === null || _a === void 0 ? void 0 : _a.some(h => entity === h))) {
                    setSelectedCircle({
                        measurementId: id,
                        screenPosition: { x: event.position.x, y: event.position.y }
                    });
                    return true;
                }
            }
        }
        return false;
    }, [viewer, measurements, circles]);
    const handleMapClick = useCallback((event) => {
        if (!isActive || !viewer || viewer.isDestroyed())
            return false;
        // If we were dragging, don't process as a click
        if (dragging)
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        // Cancel pending circle by clicking its center entity
        if (pendingCircle && (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) === pendingCircle.centerEntity) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.remove(pendingCircle.centerEntity);
            }
            setPendingCircle(null);
            setDrawingHUD(null);
            return true;
        }
        // Check if clicking on the pending start point to cancel
        if (pendingMeasurement && (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) === pendingMeasurement.startEntity) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.remove(pendingMeasurement.startEntity);
            }
            setPendingMeasurement(null);
            return true;
        }
        // Check if clicking on an existing measurement point or line
        if (checkExistingMeasurementClick(event)) {
            return true;
        }
        const position = pickTerrainPosition(event.position);
        if (!position)
            return false;
        // Circle-mode click handling
        if (measureMode === 'circle') {
            if (constraintPolygon && constraintPolygon.length >= 3) {
                const cartographic = Cesium.Cartographic.fromCartesian(position);
                const clickPoint = {
                    lat: Cesium.Math.toDegrees(cartographic.latitude),
                    lng: Cesium.Math.toDegrees(cartographic.longitude)
                };
                if (!isPointInPolygon(clickPoint, constraintPolygon))
                    return false;
            }
            // First click: place center, create ghost circle that scales with cursor
            if (!pendingCircle) {
                const centerEntity = createCenterPointEntity(position, DEFAULT_MEASUREMENT_COLOR);
                if (!centerEntity)
                    return true;
                pendingCircleRadiusRef.current = 0.01;
                const ghostCircle = createPendingCircleEntity(position, DEFAULT_MEASUREMENT_COLOR);
                setPendingCircle({ center: position, centerEntity, ghostEntity: ghostCircle !== null && ghostCircle !== void 0 ? ghostCircle : undefined });
                return true;
            }
            // Second click: commit the circle.
            // Drop the big center dot AND the on-map radius label (UX request:
            // the dialog gives the numeric radius; the on-map dot/label feel
            // like noise). Remove the ghost ellipse from mid-draw. Replace
            // with a fill ellipse + a polyline outline (so line-width actually
            // works — see createCircleOutline).
            const radiusMeters = Cesium.Cartesian3.distance(pendingCircle.center, position);
            if (dataSourceRef.current) {
                if (pendingCircle.ghostEntity) {
                    dataSourceRef.current.entities.remove(pendingCircle.ghostEntity);
                }
                dataSourceRef.current.entities.remove(pendingCircle.centerEntity);
            }
            const circleEntity = createCommittedCircleEntity(pendingCircle.center, radiusMeters, DEFAULT_MEASUREMENT_COLOR);
            const outlineEntity = createCircleOutlineEntity(pendingCircle.center, radiusMeters, DEFAULT_MEASUREMENT_COLOR);
            if (!circleEntity || !outlineEntity) {
                setPendingCircle(null);
                setDrawingHUD(null);
                return true;
            }
            const id = `measurement-circle-${Date.now()}`;
            const name = `Circle ${circleCounterRef.current}`;
            circleCounterRef.current += 1;
            const circle = {
                kind: 'circle',
                id,
                name,
                center: pendingCircle.center,
                radiusMeters,
                circleEntity,
                outlineEntity,
                color: DEFAULT_MEASUREMENT_COLOR,
                // outlineColor undefined ⇒ tracks fill color until user diverges.
                // fillOpacity defaults to the same alpha the previous hardcoded
                // material used.
                fillOpacity: DEFAULT_CIRCLE_FILL_OPACITY,
                displayUnit: defaultUnit,
                areaUnit: defaultAreaUnit,
                projectId: projectId || undefined,
            };
            pushUndoSnapshot();
            setCircles(prev => {
                const next = new Map(prev);
                next.set(id, circle);
                return next;
            });
            setPendingCircle(null);
            setDrawingHUD(null);
            setSelectedCircle({
                measurementId: id,
                screenPosition: { x: event.position.x, y: event.position.y },
            });
            return true;
        }
        // If constraint polygon is set, validate click is inside it
        if (constraintPolygon && constraintPolygon.length >= 3) {
            const cartographic = Cesium.Cartographic.fromCartesian(position);
            const clickPoint = {
                lat: Cesium.Math.toDegrees(cartographic.latitude),
                lng: Cesium.Math.toDegrees(cartographic.longitude)
            };
            if (!isPointInPolygon(clickPoint, constraintPolygon)) {
                return false;
            }
        }
        if (!pendingMeasurement) {
            const startEntity = createStartPointEntity(position, DEFAULT_MEASUREMENT_COLOR);
            if (startEntity) {
                setPendingMeasurement({
                    startPoint: position,
                    startEntity
                });
            }
            return true;
        }
        const endEntity = createEndPointEntity(position, DEFAULT_MEASUREMENT_COLOR);
        if (!endEntity) {
            if (dataSourceRef.current && pendingMeasurement.startEntity) {
                dataSourceRef.current.entities.remove(pendingMeasurement.startEntity);
            }
            setPendingMeasurement(null);
            return true;
        }
        const distance = Cesium.Cartesian3.distance(pendingMeasurement.startPoint, position);
        const bearingDeg = computeBearingDeg(pendingMeasurement.startPoint, position);
        const lineEntity = createLineEntity(pendingMeasurement.startPoint, position, DEFAULT_MEASUREMENT_COLOR);
        const labelEntity = createLabelEntity(pendingMeasurement.startPoint, position, distance, defaultUnit, DEFAULT_MEASUREMENT_COLOR, bearingDeg);
        if (!lineEntity || !labelEntity) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.remove(pendingMeasurement.startEntity);
                dataSourceRef.current.entities.remove(endEntity);
                if (lineEntity)
                    dataSourceRef.current.entities.remove(lineEntity);
            }
            setPendingMeasurement(null);
            return true;
        }
        const id = `measurement-${Date.now()}`;
        const name = `Measurement ${measurementCounterRef.current}`;
        measurementCounterRef.current += 1;
        const measurement = {
            kind: 'line',
            id,
            name,
            startPoint: pendingMeasurement.startPoint,
            endPoint: position,
            startEntity: pendingMeasurement.startEntity,
            endEntity,
            lineEntity,
            labelEntity,
            distanceMeters: distance,
            bearingDeg,
            color: DEFAULT_MEASUREMENT_COLOR,
            displayUnit: defaultUnit,
            showLabel: true,
            projectId: projectId || undefined
        };
        pushUndoSnapshot();
        setMeasurements(prev => {
            const newMap = new Map(prev);
            newMap.set(id, measurement);
            return newMap;
        });
        // Fetch elevation profile async if projectId is available
        if (projectId) {
            const gen = ++profileFetchGenRef.current;
            fetchLineElevationProfile(projectId, pendingMeasurement.startPoint, position, readyLayers)
                .then(profile => {
                if (gen !== profileFetchGenRef.current)
                    return;
                setMeasurements(prev => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(id);
                    if (existing) {
                        newMap.set(id, { ...existing, elevationProfile: profile || null });
                    }
                    return newMap;
                });
            })
                .catch(err => {
                if (gen !== profileFetchGenRef.current)
                    return;
                console.error('Failed to fetch elevation profile:', err);
                setMeasurements(prev => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(id);
                    if (existing) {
                        newMap.set(id, { ...existing, elevationProfile: null });
                    }
                    return newMap;
                });
            });
        }
        else {
            // No projectId means no DEM data available
            setMeasurements(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(id);
                if (existing) {
                    newMap.set(id, { ...existing, elevationProfile: null });
                }
                return newMap;
            });
        }
        setPendingMeasurement(null);
        setDrawingHUD(null);
        setSelectedMeasurement({
            measurementId: id,
            screenPosition: { x: event.position.x, y: event.position.y }
        });
        return true;
    }, [isActive, viewer, measurements, pendingMeasurement, dragging, pickTerrainPosition, createStartPointEntity, createEndPointEntity, createLineEntity, createLabelEntity, defaultUnit, defaultAreaUnit, projectId, constraintPolygon, measureMode, pendingCircle, checkExistingMeasurementClick, createCenterPointEntity, createPendingCircleEntity, createCommittedCircleEntity, createCircleOutlineEntity, pushUndoSnapshot]);
    // Handle mouse move for dragging and hover
    const handleMapHover = useCallback((event) => {
        var _a, _b, _c;
        if (!viewer || viewer.isDestroyed()) {
            setHoveredMeasurement(null);
            setDrawingHUD(null);
            return false;
        }
        // Live HUD while drawing the second point of a line measurement
        if (isActive && pendingMeasurement) {
            const position = pickTerrainPosition(event.endPosition);
            if (position) {
                const distance = Cesium.Cartesian3.distance(pendingMeasurement.startPoint, position);
                const startCart = Cesium.Cartographic.fromCartesian(pendingMeasurement.startPoint);
                const endCart = Cesium.Cartographic.fromCartesian(position);
                const geodesic = new Cesium.EllipsoidGeodesic(startCart, endCart);
                const bearingDeg = ((Cesium.Math.toDegrees(geodesic.startHeading) % 360) + 360) % 360;
                setDrawingHUD({
                    screenX: event.endPosition.x,
                    screenY: event.endPosition.y,
                    primaryText: formatDistance(distance, defaultUnit),
                    secondaryText: `Heading ${bearingDeg.toFixed(1)}°`,
                    rawDistanceMeters: distance,
                    rawBearingDeg: bearingDeg,
                });
            }
            // Don't return — still allow dragging/hover handling below if applicable.
        }
        else if (isActive && pendingCircle) {
            const position = pickTerrainPosition(event.endPosition);
            if (position) {
                const r = Cesium.Cartesian3.distance(pendingCircle.center, position);
                pendingCircleRadiusRef.current = r;
                const area = Math.PI * r * r;
                setDrawingHUD({
                    screenX: event.endPosition.x,
                    screenY: event.endPosition.y,
                    primaryText: `Radius ${formatDistance(r, defaultUnit)}`,
                    // Area uses the squared distance unit (m², ft², etc) so the
                    // preview is coherent with what the user is reading for the
                    // radius. The post-commit dialog still uses the user's
                    // selected area unit (acres/hectares/sqm/sqft).
                    secondaryText: `Area ${formatSquaredDistanceArea(area, defaultUnit)}`,
                    rawRadiusMeters: r,
                    rawAreaSqM: area,
                });
            }
        }
        else if (drawingHUD) {
            setDrawingHUD(null);
        }
        // Handle dragging — entity-only updates, no state change (state updated on mouse up)
        if (dragging) {
            const position = pickTerrainPosition(event.endPosition);
            if (!position)
                return true;
            if (dragging.kind === 'line-endpoint') {
                const measurement = measurements.get(dragging.measurementId);
                if (measurement) {
                    const pointEntity = dragging.pointType === 'start' ? measurement.startEntity : measurement.endEntity;
                    if (pointEntity)
                        pointEntity.position = position;
                    const newStart = dragging.pointType === 'start' ? position : measurement.startPoint;
                    const newEnd = dragging.pointType === 'end' ? position : measurement.endPoint;
                    updateDraggedEntities(dragging.measurementId, newStart, newEnd);
                }
            }
            else if (dragging.kind === 'line-translate') {
                // Translate both endpoints by the same lat/lng delta so the
                // bearing and length stay identical to the original.
                const measurement = measurements.get(dragging.measurementId);
                if (measurement) {
                    const initStartCart = Cesium.Cartographic.fromCartesian(dragging.initialStart);
                    const initEndCart = Cesium.Cartographic.fromCartesian(dragging.initialEnd);
                    const initCursorCart = Cesium.Cartographic.fromCartesian(dragging.initialCursor);
                    const cursorCart = Cesium.Cartographic.fromCartesian(position);
                    const dLat = cursorCart.latitude - initCursorCart.latitude;
                    const dLng = cursorCart.longitude - initCursorCart.longitude;
                    const newStart = Cesium.Cartesian3.fromRadians(initStartCart.longitude + dLng, initStartCart.latitude + dLat);
                    const newEnd = Cesium.Cartesian3.fromRadians(initEndCart.longitude + dLng, initEndCart.latitude + dLat);
                    if (measurement.startEntity)
                        measurement.startEntity.position = newStart;
                    if (measurement.endEntity)
                        measurement.endEntity.position = newEnd;
                    updateDraggedEntities(dragging.measurementId, newStart, newEnd);
                }
            }
            else if (dragging.kind === 'circle-center') {
                const circle = circles.get(dragging.circleId);
                if (circle) {
                    const initCenterCart = Cesium.Cartographic.fromCartesian(dragging.initialCenter);
                    const initCursorCart = Cesium.Cartographic.fromCartesian(dragging.initialCursor);
                    const cursorCart = Cesium.Cartographic.fromCartesian(position);
                    const dLat = cursorCart.latitude - initCursorCart.latitude;
                    const dLng = cursorCart.longitude - initCursorCart.longitude;
                    const newCenter = Cesium.Cartesian3.fromRadians(initCenterCart.longitude + dLng, initCenterCart.latitude + dLat);
                    // Move center point + ellipse fill + label; rebuild the outline
                    // polyline ring around the new center.
                    if (circle.centerEntity)
                        circle.centerEntity.position = newCenter;
                    circle.circleEntity.position = newCenter;
                    if (circle.labelEntity)
                        circle.labelEntity.position = newCenter;
                    if ((_a = circle.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
                        // Re-sample the ring at the new center to keep the outline
                        // tracking the fill. Cheap because the ring is 64 points.
                        const newRing = computeCircleRing(newCenter, circle.radiusMeters);
                        circle.outlineEntity.polyline.positions = new Cesium.ConstantProperty(newRing);
                    }
                    if (circle.resizeHandles) {
                        const cardinals = computeCardinalPoints(newCenter, circle.radiusMeters);
                        circle.resizeHandles.forEach((h, idx) => {
                            if (h && cardinals[idx])
                                h.position = cardinals[idx];
                        });
                    }
                }
            }
            else if (dragging.kind === 'circle-resize') {
                const circle = circles.get(dragging.circleId);
                if (circle) {
                    // Radius is just the distance from the (unchanged) center to
                    // the cursor. The grabbed cardinal handle naturally lands under
                    // the cursor because we re-place all four at every move.
                    const newRadius = Math.max(0.01, Cesium.Cartesian3.distance(circle.center, position));
                    if (circle.circleEntity.ellipse) {
                        circle.circleEntity.ellipse.semiMajorAxis = new Cesium.ConstantProperty(newRadius);
                        circle.circleEntity.ellipse.semiMinorAxis = new Cesium.ConstantProperty(newRadius);
                    }
                    if ((_b = circle.outlineEntity) === null || _b === void 0 ? void 0 : _b.polyline) {
                        const newRing = computeCircleRing(circle.center, newRadius);
                        circle.outlineEntity.polyline.positions = new Cesium.ConstantProperty(newRing);
                    }
                    if ((_c = circle.labelEntity) === null || _c === void 0 ? void 0 : _c.label) {
                        circle.labelEntity.label.text = new Cesium.ConstantProperty(formatCircleLabel(newRadius, circle.displayUnit, circle.areaUnit));
                    }
                    if (circle.resizeHandles) {
                        const cardinals = computeCardinalPoints(circle.center, newRadius);
                        circle.resizeHandles.forEach((h, idx) => {
                            if (h && cardinals[idx])
                                h.position = cardinals[idx];
                        });
                    }
                }
            }
            return true;
        }
        // Normal hover behavior requires active tool
        if (!isActive || !projectId) {
            setHoveredMeasurement(null);
            return false;
        }
        const pickedObject = viewer.scene.pick(event.endPosition);
        if (!(pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id)) {
            setHoveredMeasurement(null);
            return false;
        }
        const entity = pickedObject.id;
        for (const [id, measurement] of measurements.entries()) {
            if (entity === measurement.lineEntity) {
                const position = pickTerrainPosition(event.endPosition);
                if (!position) {
                    setHoveredMeasurement(null);
                    return false;
                }
                const distanceFromStart = Cesium.Cartesian3.distance(measurement.startPoint, position);
                if (elevationDebounceRef.current) {
                    clearTimeout(elevationDebounceRef.current);
                }
                const cartographic = Cesium.Cartographic.fromCartesian(position);
                const lat = Cesium.Math.toDegrees(cartographic.latitude);
                const lng = Cesium.Math.toDegrees(cartographic.longitude);
                setHoveredMeasurement({
                    measurementId: id,
                    elevation: undefined, // undefined = loading
                    distanceFromStart,
                    screenPosition: { x: event.endPosition.x, y: event.endPosition.y },
                    unit: measurement.displayUnit
                });
                elevationDebounceRef.current = setTimeout(async () => {
                    try {
                        const elevation = await elevationService.sampleElevation(projectId, lat, lng);
                        setHoveredMeasurement(prev => {
                            if ((prev === null || prev === void 0 ? void 0 : prev.measurementId) === id) {
                                // null from service means no data available
                                return { ...prev, elevation: elevation };
                            }
                            return prev;
                        });
                    }
                    catch (_a) {
                        // Error fetching - set to null (no data)
                        setHoveredMeasurement(prev => {
                            if ((prev === null || prev === void 0 ? void 0 : prev.measurementId) === id) {
                                return { ...prev, elevation: null };
                            }
                            return prev;
                        });
                    }
                }, 300);
                return true;
            }
        }
        setHoveredMeasurement(null);
        return false;
    }, [isActive, viewer, projectId, measurements, dragging, pendingMeasurement, pendingCircle, drawingHUD, defaultUnit, defaultAreaUnit, pickTerrainPosition, updateDraggedEntities]);
    // Handle mouse up to end dragging — finalize state from entity positions.
    // Branches by drag kind so each mode commits the right thing.
    const handleMapMouseUp = useCallback(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        if (!dragging || !viewer || viewer.isDestroyed())
            return false;
        enableCameraControls(viewer);
        const commitLineState = (measurementId, newStart, newEnd) => {
            const newDistance = Cesium.Cartesian3.distance(newStart, newEnd);
            const newBearing = computeBearingDeg(newStart, newEnd);
            setMeasurements(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(measurementId);
                if (existing) {
                    newMap.set(measurementId, {
                        ...existing,
                        startPoint: newStart,
                        endPoint: newEnd,
                        distanceMeters: newDistance,
                        bearingDeg: newBearing,
                        elevationProfile: undefined,
                    });
                }
                return newMap;
            });
            if (projectId) {
                const gen = ++profileFetchGenRef.current;
                fetchLineElevationProfile(projectId, newStart, newEnd, readyLayers)
                    .then(profile => {
                    if (gen !== profileFetchGenRef.current)
                        return;
                    setMeasurements(prev => {
                        const newMap = new Map(prev);
                        const existing = newMap.get(measurementId);
                        if (existing) {
                            newMap.set(measurementId, { ...existing, elevationProfile: profile || null });
                        }
                        return newMap;
                    });
                })
                    .catch(err => {
                    if (gen !== profileFetchGenRef.current)
                        return;
                    console.error('Failed to fetch elevation profile after drag:', err);
                    setMeasurements(prev => {
                        const newMap = new Map(prev);
                        const existing = newMap.get(measurementId);
                        if (existing) {
                            newMap.set(measurementId, { ...existing, elevationProfile: null });
                        }
                        return newMap;
                    });
                });
            }
        };
        if (dragging.kind === 'line-endpoint') {
            const measurement = measurements.get(dragging.measurementId);
            if (measurement) {
                const draggedEntity = dragging.pointType === 'start' ? measurement.startEntity : measurement.endEntity;
                const newPosition = (_b = (_a = draggedEntity === null || draggedEntity === void 0 ? void 0 : draggedEntity.position) === null || _a === void 0 ? void 0 : _a.getValue) === null || _b === void 0 ? void 0 : _b.call(_a, Cesium.JulianDate.now());
                if (newPosition) {
                    const newStart = dragging.pointType === 'start' ? newPosition : measurement.startPoint;
                    const newEnd = dragging.pointType === 'end' ? newPosition : measurement.endPoint;
                    // Only record an undo step if the endpoint actually moved (a click
                    // without a drag commits the same position — no edit to undo).
                    const oldPos = dragging.pointType === 'start' ? measurement.startPoint : measurement.endPoint;
                    if (Cesium.Cartesian3.distance(newPosition, oldPos) > 0.01)
                        pushUndoSnapshot();
                    commitLineState(dragging.measurementId, newStart, newEnd);
                }
            }
        }
        else if (dragging.kind === 'line-translate') {
            const measurement = measurements.get(dragging.measurementId);
            if (measurement) {
                const newStart = (_e = (_d = (_c = measurement.startEntity) === null || _c === void 0 ? void 0 : _c.position) === null || _d === void 0 ? void 0 : _d.getValue) === null || _e === void 0 ? void 0 : _e.call(_d, Cesium.JulianDate.now());
                const newEnd = (_h = (_g = (_f = measurement.endEntity) === null || _f === void 0 ? void 0 : _f.position) === null || _g === void 0 ? void 0 : _g.getValue) === null || _h === void 0 ? void 0 : _h.call(_g, Cesium.JulianDate.now());
                if (newStart && newEnd) {
                    if (Cesium.Cartesian3.distance(newStart, measurement.startPoint) > 0.01)
                        pushUndoSnapshot();
                    commitLineState(dragging.measurementId, newStart, newEnd);
                }
            }
        }
        else if (dragging.kind === 'circle-center') {
            const circle = circles.get(dragging.circleId);
            if (circle) {
                const newCenter = (_m = (_l = (_k = (_j = circle.centerEntity) === null || _j === void 0 ? void 0 : _j.position) === null || _k === void 0 ? void 0 : _k.getValue) === null || _l === void 0 ? void 0 : _l.call(_k, Cesium.JulianDate.now())) !== null && _m !== void 0 ? _m : (_q = (_p = (_o = circle.circleEntity) === null || _o === void 0 ? void 0 : _o.position) === null || _p === void 0 ? void 0 : _p.getValue) === null || _q === void 0 ? void 0 : _q.call(_p, Cesium.JulianDate.now());
                if (newCenter) {
                    if (Cesium.Cartesian3.distance(newCenter, circle.center) > 0.01)
                        pushUndoSnapshot();
                    setCircles(prev => {
                        const next = new Map(prev);
                        const existing = next.get(dragging.circleId);
                        if (existing)
                            next.set(dragging.circleId, { ...existing, center: newCenter });
                        return next;
                    });
                }
            }
        }
        else if (dragging.kind === 'circle-resize') {
            const circle = circles.get(dragging.circleId);
            if (circle) {
                // The live drag mutated the ellipse axis props directly; read the
                // final value back to commit it to state.
                const finalRadius = (_u = (_t = (_s = (_r = circle.circleEntity.ellipse) === null || _r === void 0 ? void 0 : _r.semiMajorAxis) === null || _s === void 0 ? void 0 : _s.getValue) === null || _t === void 0 ? void 0 : _t.call(_s, Cesium.JulianDate.now())) !== null && _u !== void 0 ? _u : circle.radiusMeters;
                if (Math.abs(finalRadius - circle.radiusMeters) > 0.01)
                    pushUndoSnapshot();
                setCircles(prev => {
                    const next = new Map(prev);
                    const existing = next.get(dragging.circleId);
                    if (existing)
                        next.set(dragging.circleId, { ...existing, radiusMeters: finalRadius });
                    return next;
                });
            }
        }
        setDragging(null);
        return true;
    }, [dragging, viewer, measurements, circles, projectId, pushUndoSnapshot]);
    const removeMeasurement = useCallback((id) => {
        const measurement = measurements.get(id);
        if (!measurement || !dataSourceRef.current)
            return;
        pushUndoSnapshot();
        dataSourceRef.current.entities.remove(measurement.startEntity);
        dataSourceRef.current.entities.remove(measurement.endEntity);
        dataSourceRef.current.entities.remove(measurement.lineEntity);
        dataSourceRef.current.entities.remove(measurement.labelEntity);
        setMeasurements(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
        });
        if ((selectedMeasurement === null || selectedMeasurement === void 0 ? void 0 : selectedMeasurement.measurementId) === id) {
            setSelectedMeasurement(null);
        }
    }, [measurements, selectedMeasurement, pushUndoSnapshot]);
    const updateMeasurementColor = useCallback((id, colorHex) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return;
        const color = Cesium.Color.fromCssColorString(colorHex);
        // Start point: solid color fill with black outline
        if (measurement.startEntity.point) {
            measurement.startEntity.point.color = new Cesium.ConstantProperty(color);
            measurement.startEntity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
        }
        // End point: black fill with colored outline
        if (measurement.endEntity.point) {
            measurement.endEntity.point.color = new Cesium.ConstantProperty(Cesium.Color.BLACK);
            measurement.endEntity.point.outlineColor = new Cesium.ConstantProperty(color);
        }
        if (measurement.lineEntity.polyline) {
            measurement.lineEntity.polyline.material = new Cesium.ColorMaterialProperty(color);
        }
        setMeasurements(prev => {
            const newMap = new Map(prev);
            newMap.set(id, { ...measurement, color: colorHex });
            return newMap;
        });
    }, [measurements]);
    const updateMeasurementLineWidth = useCallback((id, width) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return;
        if (measurement.lineEntity.polyline) {
            measurement.lineEntity.polyline.width = viewer
                ? makePolylineWidthProperty(width, viewer)
                : new Cesium.ConstantProperty(width);
        }
        setMeasurements(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(id);
            if (existing)
                newMap.set(id, { ...existing, lineWidth: width });
            return newMap;
        });
    }, [measurements, viewer]);
    const updateMeasurementUnit = useCallback((id, unit) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return;
        setDefaultUnit(unit);
        if (measurement.labelEntity.label) {
            measurement.labelEntity.label.text = new Cesium.ConstantProperty(formatLineLabel(measurement.distanceMeters, unit, measurement.bearingDeg));
        }
        setMeasurements(prev => {
            const newMap = new Map(prev);
            newMap.set(id, { ...measurement, displayUnit: unit });
            return newMap;
        });
    }, [measurements]);
    const updateMeasurementName = useCallback((id, name) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return;
        setMeasurements(prev => {
            const newMap = new Map(prev);
            newMap.set(id, { ...measurement, name });
            return newMap;
        });
    }, [measurements]);
    const updateMeasurementColorBy = useCallback((id, mode) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return;
        setMeasurements(prev => {
            const next = new Map(prev);
            next.set(id, { ...measurement, colorBy: mode });
            return next;
        });
    }, [measurements]);
    const updateMeasurementShowLabel = useCallback((id, show) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return;
        if (measurement.labelEntity)
            measurement.labelEntity.show = show;
        setMeasurements(prev => {
            const next = new Map(prev);
            next.set(id, { ...measurement, showLabel: show });
            return next;
        });
    }, [measurements]);
    const updateMeasurementOpacity = useCallback((id, opacity) => {
        var _a, _b, _c, _d;
        const m = measurements.get(id);
        if (!m)
            return;
        const baseColor = Cesium.Color.fromCssColorString(m.color);
        if ((_a = m.startEntity) === null || _a === void 0 ? void 0 : _a.point) {
            m.startEntity.point.color = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
        }
        if ((_b = m.endEntity) === null || _b === void 0 ? void 0 : _b.point) {
            m.endEntity.point.color = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
            m.endEntity.point.outlineColor = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
        }
        if ((_c = m.lineEntity) === null || _c === void 0 ? void 0 : _c.polyline) {
            m.lineEntity.polyline.material = new Cesium.ColorMaterialProperty(baseColor.withAlpha(opacity));
        }
        if ((_d = m.labelEntity) === null || _d === void 0 ? void 0 : _d.label) {
            m.labelEntity.label.fillColor = new Cesium.ConstantProperty(Cesium.Color.WHITE.withAlpha(opacity));
            m.labelEntity.label.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
        }
        setItemOpacities(prev => {
            const newMap = new Map(prev);
            newMap.set(id, opacity);
            return newMap;
        });
    }, [measurements]);
    const getItemOpacity = (id) => {
        var _a;
        return (_a = itemOpacities.get(id)) !== null && _a !== void 0 ? _a : 1;
    };
    const clearAllMeasurements = useCallback(() => {
        if (!dataSourceRef.current)
            return;
        dataSourceRef.current.entities.removeAll();
        setMeasurements(new Map());
        setCircles(new Map());
        setPendingMeasurement(null);
        setPendingCircle(null);
        setSelectedMeasurement(null);
        setSelectedCircle(null);
        setHoveredMeasurement(null);
        setDrawingHUD(null);
        setDragging(null);
    }, []);
    const cancelPending = useCallback(() => {
        if (!dataSourceRef.current)
            return;
        if (pendingMeasurement) {
            dataSourceRef.current.entities.remove(pendingMeasurement.startEntity);
            setPendingMeasurement(null);
        }
        if (pendingCircle) {
            dataSourceRef.current.entities.remove(pendingCircle.centerEntity);
            if (pendingCircle.ghostEntity) {
                dataSourceRef.current.entities.remove(pendingCircle.ghostEntity);
            }
            setPendingCircle(null);
        }
        setDrawingHUD(null);
    }, [pendingMeasurement, pendingCircle]);
    const removeCircle = useCallback((id) => {
        const circle = circles.get(id);
        if (!circle || !dataSourceRef.current)
            return;
        pushUndoSnapshot();
        if (circle.centerEntity)
            dataSourceRef.current.entities.remove(circle.centerEntity);
        dataSourceRef.current.entities.remove(circle.circleEntity);
        if (circle.outlineEntity)
            dataSourceRef.current.entities.remove(circle.outlineEntity);
        if (circle.labelEntity)
            dataSourceRef.current.entities.remove(circle.labelEntity);
        if (circle.resizeHandles) {
            circle.resizeHandles.forEach(h => { var _a; return (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(h); });
        }
        setCircles(prev => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
        if ((selectedCircle === null || selectedCircle === void 0 ? void 0 : selectedCircle.measurementId) === id)
            setSelectedCircle(null);
    }, [circles, selectedCircle, pushUndoSnapshot]);
    // Fill color. Outline polyline tracks fill ONLY while outlineColor is
    // undiverged (undefined). Mirrors polygon dialog semantics.
    const updateCircleColor = useCallback((id, colorHex) => {
        var _a, _b, _c;
        const circle = circles.get(id);
        if (!circle)
            return;
        const color = Cesium.Color.fromCssColorString(colorHex);
        const opacity = (_a = circle.fillOpacity) !== null && _a !== void 0 ? _a : DEFAULT_CIRCLE_FILL_OPACITY;
        if ((_b = circle.centerEntity) === null || _b === void 0 ? void 0 : _b.point) {
            circle.centerEntity.point.color = new Cesium.ConstantProperty(color);
        }
        if (circle.circleEntity.ellipse) {
            circle.circleEntity.ellipse.material = new Cesium.ColorMaterialProperty(color.withAlpha(opacity));
        }
        if (((_c = circle.outlineEntity) === null || _c === void 0 ? void 0 : _c.polyline) && circle.outlineColor === undefined) {
            circle.outlineEntity.polyline.material = new Cesium.ColorMaterialProperty(color);
        }
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, { ...circle, color: colorHex });
            return next;
        });
    }, [circles]);
    const updateCircleOutlineColor = useCallback((id, colorHex) => {
        var _a;
        const circle = circles.get(id);
        if (!circle)
            return;
        const color = Cesium.Color.fromCssColorString(colorHex);
        if ((_a = circle.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
            circle.outlineEntity.polyline.material = new Cesium.ColorMaterialProperty(color);
        }
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, { ...circle, outlineColor: colorHex });
            return next;
        });
    }, [circles]);
    const updateCircleFillOpacity = useCallback((id, opacity) => {
        const circle = circles.get(id);
        if (!circle)
            return;
        const fillColor = Cesium.Color.fromCssColorString(circle.color);
        if (circle.circleEntity.ellipse) {
            circle.circleEntity.ellipse.material = new Cesium.ColorMaterialProperty(fillColor.withAlpha(opacity));
        }
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, { ...circle, fillOpacity: opacity });
            return next;
        });
    }, [circles]);
    const updateCircleLineWidth = useCallback((id, width) => {
        var _a;
        const circle = circles.get(id);
        if (!circle)
            return;
        // Outline polyline width — reliably honored across browsers (unlike
        // ellipse.outlineWidth which is clamped to 1px).
        if ((_a = circle.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
            circle.outlineEntity.polyline.width = viewer
                ? makePolylineWidthProperty(width, viewer)
                : new Cesium.ConstantProperty(width);
        }
        setCircles(prev => {
            const next = new Map(prev);
            const existing = next.get(id);
            if (existing)
                next.set(id, { ...existing, lineWidth: width });
            return next;
        });
    }, [circles, viewer]);
    const updateCircleDisplayUnit = useCallback((id, unit) => {
        var _a;
        const circle = circles.get(id);
        if (!circle)
            return;
        setDefaultUnit(unit);
        if ((_a = circle.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
            circle.labelEntity.label.text = new Cesium.ConstantProperty(formatCircleLabel(circle.radiusMeters, unit, circle.areaUnit));
        }
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, { ...circle, displayUnit: unit });
            return next;
        });
    }, [circles]);
    const updateCircleAreaUnit = useCallback((id, unit) => {
        var _a;
        const circle = circles.get(id);
        if (!circle)
            return;
        setDefaultAreaUnit(unit);
        if ((_a = circle.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
            circle.labelEntity.label.text = new Cesium.ConstantProperty(formatCircleLabel(circle.radiusMeters, circle.displayUnit, unit));
        }
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, { ...circle, areaUnit: unit });
            return next;
        });
    }, [circles]);
    // Show / hide the circle's on-map label. Lazy-creates the label entity
    // on first 'show' so circles created with showLabel=false don't carry
    // a hidden entity around.
    const updateCircleShowLabel = useCallback((id, show) => {
        const circle = circles.get(id);
        if (!circle || !dataSourceRef.current)
            return;
        let labelEntity = circle.labelEntity;
        if (show) {
            if (!labelEntity) {
                labelEntity = createCircleLabel(dataSourceRef.current, circle.center, formatCircleLabel(circle.radiusMeters, circle.displayUnit, circle.areaUnit));
            }
            else {
                labelEntity.show = true;
                if (labelEntity.label) {
                    labelEntity.label.text = new Cesium.ConstantProperty(formatCircleLabel(circle.radiusMeters, circle.displayUnit, circle.areaUnit));
                }
            }
        }
        else if (labelEntity) {
            labelEntity.show = false;
        }
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, { ...circle, labelEntity, showLabel: show });
            return next;
        });
    }, [circles]);
    const updateCircleName = useCallback((id, name) => {
        const circle = circles.get(id);
        if (!circle)
            return;
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, { ...circle, name });
            return next;
        });
    }, [circles]);
    const dismissCircleSelection = useCallback(() => {
        setSelectedCircle(null);
    }, []);
    const dismissSelection = useCallback(() => {
        setSelectedMeasurement(null);
    }, []);
    const selectMeasurementById = useCallback((measurementId, screenPosition) => {
        // No existence check: caller may invoke right after a state-updating create.
        setSelectedMeasurement({
            measurementId,
            screenPosition: screenPosition !== null && screenPosition !== void 0 ? screenPosition : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        });
    }, []);
    const selectCircleById = useCallback((circleId, screenPosition) => {
        setSelectedCircle({
            measurementId: circleId,
            screenPosition: screenPosition !== null && screenPosition !== void 0 ? screenPosition : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        });
    }, []);
    const addCircleFromPoints = useCallback((centerLat, centerLng, radiusMeters, name, color, lineWidth, areaUnit, outlineColor, fillOpacity, showLabel) => {
        if (!dataSourceRef.current)
            return null;
        const measureColor = color || DEFAULT_MEASUREMENT_COLOR;
        const center = Cesium.Cartesian3.fromDegrees(centerLng, centerLat);
        const effectiveAreaUnit = areaUnit !== null && areaUnit !== void 0 ? areaUnit : defaultAreaUnit;
        const circleEntity = createCommittedCircleEntity(center, radiusMeters, measureColor, fillOpacity);
        const outlineEntity = createCircleOutlineEntity(center, radiusMeters, outlineColor !== null && outlineColor !== void 0 ? outlineColor : measureColor, lineWidth);
        if (!circleEntity || !outlineEntity)
            return null;
        const effectiveShowLabel = showLabel !== null && showLabel !== void 0 ? showLabel : false;
        let labelEntity;
        if (effectiveShowLabel) {
            labelEntity = createCircleLabel(dataSourceRef.current, center, formatCircleLabel(radiusMeters, defaultUnit, effectiveAreaUnit));
        }
        const id = `measurement-circle-${Date.now()}`;
        const circleName = name || `Circle ${circleCounterRef.current}`;
        if (!name)
            circleCounterRef.current += 1;
        const circle = {
            kind: 'circle',
            id,
            name: circleName,
            center,
            radiusMeters,
            circleEntity,
            outlineEntity,
            labelEntity,
            color: measureColor,
            outlineColor,
            fillOpacity: fillOpacity !== null && fillOpacity !== void 0 ? fillOpacity : DEFAULT_CIRCLE_FILL_OPACITY,
            showLabel: effectiveShowLabel,
            displayUnit: defaultUnit,
            areaUnit: effectiveAreaUnit,
            projectId: projectId || undefined,
            lineWidth,
        };
        setCircles(prev => {
            const next = new Map(prev);
            next.set(id, circle);
            return next;
        });
        return id;
    }, [createCommittedCircleEntity, createCircleOutlineEntity, defaultUnit, defaultAreaUnit, projectId]);
    useEffect(() => {
        if (!isActive && (pendingMeasurement || pendingCircle)) {
            cancelPending();
        }
    }, [isActive, pendingMeasurement, pendingCircle, cancelPending]);
    useEffect(() => {
        if (selectedMeasurement && !isItemVisible(selectedMeasurement.measurementId)) {
            setSelectedMeasurement(null);
        }
    }, [selectedMeasurement, isItemVisible]);
    // Line endpoint handles show only while the measurement is selected (actively
    // editable). A deselected line reads as a clean segment with no end dots; the
    // line itself is never touched here, so it stays visible. Circle handles are
    // already ephemeral (mounted on select, removed on deselect) so they comply.
    // Drawing-in-progress endpoints live on pendingMeasurement, so they're unaffected.
    useEffect(() => {
        const selId = selectedMeasurement === null || selectedMeasurement === void 0 ? void 0 : selectedMeasurement.measurementId;
        measurements.forEach((m, id) => {
            const showHandles = id === selId;
            if (m.startEntity)
                m.startEntity.show = showHandles;
            if (m.endEntity)
                m.endEntity.show = showHandles;
        });
    }, [selectedMeasurement === null || selectedMeasurement === void 0 ? void 0 : selectedMeasurement.measurementId, measurements]);
    useEffect(() => {
        return () => {
            if (elevationDebounceRef.current) {
                clearTimeout(elevationDebounceRef.current);
            }
        };
    }, []);
    // Selected-circle edit handles. When a circle is selected, drop:
    //   - one center dot for translating the whole circle
    //   - four cardinal (N/E/S/W) dots on the perimeter for resizing
    // Both kinds are removed on deselect. The handles are ephemeral
    // entities tracked on the circle record so the drag handlers can pick
    // them and the move/up handlers can reposition them.
    useEffect(() => {
        if (!dataSourceRef.current)
            return;
        const ds = dataSourceRef.current;
        if (!selectedCircle)
            return;
        const circle = circles.get(selectedCircle.measurementId);
        if (!circle)
            return;
        if (circle.centerEntity || circle.resizeHandles)
            return; // already mounted
        const fillColor = Cesium.Color.fromCssColorString(circle.color);
        const centerHandle = ds.entities.add({
            position: circle.center,
            point: {
                pixelSize: 8,
                color: fillColor.withAlpha(0.9),
                outlineColor: Cesium.Color.BLACK.withAlpha(0.4),
                outlineWidth: 1,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
        });
        // Cardinal grab handles. Slightly larger than the center dot and in
        // a contrasting white-on-black scheme so the resize affordance reads
        // distinctly from the translate affordance.
        const cardinals = computeCardinalPoints(circle.center, circle.radiusMeters);
        const resizeHandles = cardinals.map(pos => ds.entities.add({
            position: pos,
            point: {
                pixelSize: 9,
                color: Cesium.Color.WHITE,
                outlineColor: Cesium.Color.BLACK,
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
        }));
        setCircles(prev => {
            const next = new Map(prev);
            const existing = next.get(selectedCircle.measurementId);
            if (existing) {
                next.set(selectedCircle.measurementId, {
                    ...existing,
                    centerEntity: centerHandle,
                    resizeHandles,
                });
            }
            return next;
        });
        return () => {
            ds.entities.remove(centerHandle);
            resizeHandles.forEach(h => ds.entities.remove(h));
            setCircles(prev => {
                const next = new Map(prev);
                const existing = next.get(selectedCircle.measurementId);
                if (existing && existing.centerEntity === centerHandle) {
                    next.set(selectedCircle.measurementId, {
                        ...existing,
                        centerEntity: undefined,
                        resizeHandles: undefined,
                    });
                }
                return next;
            });
        };
        // intentionally not depending on `circles` — we only want to react
        // to the selection itself; the handles persist through unrelated
        // circles-map updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCircle === null || selectedCircle === void 0 ? void 0 : selectedCircle.measurementId]);
    const popOutProfile = useCallback((measurementId, position) => {
        setPoppedOutProfiles(prev => {
            // Don't add if already popped out
            if (prev.some(p => p.measurementId === measurementId)) {
                return prev;
            }
            return [...prev, { measurementId, position }];
        });
    }, []);
    const closePopOutProfile = useCallback((measurementId) => {
        setPoppedOutProfiles(prev => prev.filter(p => p.measurementId !== measurementId));
    }, []);
    const restoreFromConfig = useCallback((configs, startHidden = false) => {
        var _a, _b, _c, _d;
        clearAllMeasurements();
        for (const cfg of configs) {
            if (cfg.kind === 'circle') {
                const center = Cesium.Cartesian3.fromDegrees(cfg.centerLng, cfg.centerLat);
                const circleEntity = createCommittedCircleEntity(center, cfg.radiusMeters, cfg.color, cfg.fillOpacity);
                const outlineEntity = createCircleOutlineEntity(center, cfg.radiusMeters, (_a = cfg.outlineColor) !== null && _a !== void 0 ? _a : cfg.color, cfg.lineWidth);
                if (!circleEntity || !outlineEntity)
                    continue;
                const restoredShowLabel = (_b = cfg.showLabel) !== null && _b !== void 0 ? _b : false;
                let labelEntity;
                if (restoredShowLabel && dataSourceRef.current) {
                    labelEntity = createCircleLabel(dataSourceRef.current, center, formatCircleLabel(cfg.radiusMeters, cfg.unit, cfg.areaUnit));
                }
                if (startHidden) {
                    circleEntity.show = false;
                    outlineEntity.show = false;
                    if (labelEntity)
                        labelEntity.show = false;
                }
                const circleName = cfg.name || `Circle ${circleCounterRef.current}`;
                if (!cfg.name)
                    circleCounterRef.current += 1;
                const circle = {
                    kind: 'circle',
                    id: cfg.id,
                    name: circleName,
                    center,
                    radiusMeters: cfg.radiusMeters,
                    circleEntity,
                    outlineEntity,
                    labelEntity,
                    color: cfg.color,
                    outlineColor: cfg.outlineColor,
                    fillOpacity: (_c = cfg.fillOpacity) !== null && _c !== void 0 ? _c : DEFAULT_CIRCLE_FILL_OPACITY,
                    showLabel: restoredShowLabel,
                    displayUnit: cfg.unit,
                    areaUnit: cfg.areaUnit,
                    projectId: cfg.projectId,
                    lineWidth: cfg.lineWidth,
                };
                setCircles(prev => {
                    const next = new Map(prev);
                    next.set(cfg.id, circle);
                    return next;
                });
                continue;
            }
            const line = cfg;
            const startPos = Cesium.Cartesian3.fromDegrees(line.startLng, line.startLat);
            const endPos = Cesium.Cartesian3.fromDegrees(line.endLng, line.endLat);
            const startEntity = createStartPointEntity(startPos, line.color);
            const endEntity = createEndPointEntity(endPos, line.color);
            const lineEntity = createLineEntity(startPos, endPos, line.color);
            if (!startEntity || !endEntity || !lineEntity)
                continue;
            const distance = Cesium.Cartesian3.distance(startPos, endPos);
            const bearingDeg = computeBearingDeg(startPos, endPos);
            const unit = (line.unit === 'm' || line.unit === 'ft') ? line.unit : defaultUnit;
            const labelEntity = createLabelEntity(startPos, endPos, distance, unit, line.color, bearingDeg);
            if (!labelEntity)
                continue;
            const restoredShowLabel = (_d = line.showLabel) !== null && _d !== void 0 ? _d : true;
            if (startHidden || !restoredShowLabel) {
                labelEntity.show = false;
            }
            if (startHidden) {
                startEntity.show = false;
                endEntity.show = false;
                lineEntity.show = false;
            }
            const measurementName = line.name || `Measurement ${measurementCounterRef.current}`;
            if (!line.name) {
                measurementCounterRef.current += 1;
            }
            const measurement = {
                kind: 'line',
                id: line.id,
                name: measurementName,
                startPoint: startPos,
                endPoint: endPos,
                startEntity,
                endEntity,
                lineEntity,
                labelEntity,
                distanceMeters: distance,
                bearingDeg,
                color: line.color,
                displayUnit: unit,
                showLabel: restoredShowLabel,
                projectId: line.projectId,
                elevationProfile: undefined
            };
            setMeasurements(prev => {
                const newMap = new Map(prev);
                newMap.set(line.id, measurement);
                return newMap;
            });
            if (projectId) {
                const gen = ++profileFetchGenRef.current;
                const measurementId = line.id;
                fetchLineElevationProfile(projectId, startPos, endPos, readyLayers)
                    .then(profile => {
                    if (gen !== profileFetchGenRef.current)
                        return;
                    setMeasurements(prev => {
                        const newMap = new Map(prev);
                        const existing = newMap.get(measurementId);
                        if (existing) {
                            newMap.set(measurementId, { ...existing, elevationProfile: profile || null });
                        }
                        return newMap;
                    });
                })
                    .catch(() => {
                    if (gen !== profileFetchGenRef.current)
                        return;
                    setMeasurements(prev => {
                        const newMap = new Map(prev);
                        const existing = newMap.get(measurementId);
                        if (existing) {
                            newMap.set(measurementId, { ...existing, elevationProfile: null });
                        }
                        return newMap;
                    });
                });
            }
        }
    }, [clearAllMeasurements, createStartPointEntity, createEndPointEntity, createLineEntity, createLabelEntity, defaultUnit, projectId, createCommittedCircleEntity, createCircleOutlineEntity]);
    const serializeForStorage = useCallback(() => {
        const existingJson = localStorage.getItem(STORAGE_KEY);
        const merged = (function* () {
            for (const m of measurements.values())
                yield m;
            for (const c of circles.values())
                yield c;
        })();
        return serializeMeasurementsForStorage(merged, projectId, existingJson);
    }, [measurements, circles, projectId]);
    const restoreFromStorage = useCallback((stored) => {
        if (!projectId)
            return;
        const configs = deserializeStoredMeasurements(stored, projectId);
        restoreFromConfig(configs, true);
    }, [restoreFromConfig, projectId]);
    const { saveToStorage } = useLocalStoragePersistence({
        storageKey: STORAGE_KEY,
        serialize: serializeForStorage,
        restore: restoreFromStorage,
        isReady: !!dataSourceRef.current && !!projectId,
        resetKey: projectId
    });
    useEffect(() => {
        saveToStorage();
    }, [measurements, saveToStorage]);
    // Rebuild all measurements from a geometry snapshot (used by undo/redo).
    // restoreFromConfig clears the live set first, so removed items disappear and
    // moved ones snap back. The current selection is preserved across the rebuild
    // so the dialog / resize handles stay put when you undo an edit.
    const restoreGeometrySnapshot = useCallback((configs) => {
        const keepLine = selectedMeasurementRef.current;
        const keepCircle = selectedCircleRef.current;
        restoreFromConfig(configs);
        if (keepLine && configs.some(c => c.kind === 'line' && c.id === keepLine.measurementId)) {
            selectMeasurementById(keepLine.measurementId, keepLine.screenPosition);
        }
        else if (keepCircle && configs.some(c => c.kind === 'circle' && c.id === keepCircle.measurementId)) {
            selectCircleById(keepCircle.measurementId, keepCircle.screenPosition);
        }
    }, [restoreFromConfig, selectMeasurementById, selectCircleById]);
    const undoEdit = useCallback(() => {
        if (undoStackRef.current.length === 0)
            return;
        redoStackRef.current.push(captureConfigs());
        const snap = undoStackRef.current.pop();
        restoreGeometrySnapshot(snap);
    }, [captureConfigs, restoreGeometrySnapshot]);
    const redoEdit = useCallback(() => {
        if (redoStackRef.current.length === 0)
            return;
        undoStackRef.current.push(captureConfigs());
        const snap = redoStackRef.current.pop();
        restoreGeometrySnapshot(snap);
    }, [captureConfigs, restoreGeometrySnapshot]);
    // Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z redo — scoped to when the measure tool is
    // armed or a measurement/circle is selected, so it doesn't hijack the shortcut
    // elsewhere. Suppressed while typing.
    useEffect(() => {
        const onKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z')
                return;
            if (!isActive && !selectedMeasurement && !selectedCircle)
                return;
            const target = e.target;
            const inEditable = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
            if (inEditable)
                return;
            e.preventDefault();
            if (e.shiftKey)
                redoEdit();
            else
                undoEdit();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive, selectedMeasurement, selectedCircle, undoEdit, redoEdit]);
    // Subscribe to scope events for clear and restore operations
    useEventSubscribe('scope:clearAll', () => {
        clearAllMeasurements();
    }, [clearAllMeasurements]);
    useEventSubscribe('scope:restore', (payload) => {
        if (payload.measurements && payload.measurements.length > 0) {
            const configs = payload.measurements.map(m => ({
                kind: 'line',
                id: m.id,
                name: m.name,
                startLat: m.startLat,
                startLng: m.startLng,
                endLat: m.endLat,
                endLng: m.endLng,
                color: m.color,
                unit: m.unit,
            }));
            restoreFromConfig(configs);
        }
    }, [restoreFromConfig]);
    const isEntityDraggable = useCallback((entity) => {
        var _a;
        for (const [, measurement] of measurements.entries()) {
            if (entity === measurement.startEntity || entity === measurement.endEntity)
                return true;
            if (entity === measurement.lineEntity)
                return true;
        }
        // Circle center handle + cardinal resize handles are only draggable
        // while the circle is selected.
        if (selectedCircle) {
            const circle = circles.get(selectedCircle.measurementId);
            if (circle) {
                if (entity === circle.centerEntity)
                    return true;
                if ((_a = circle.resizeHandles) === null || _a === void 0 ? void 0 : _a.some(h => entity === h))
                    return true;
            }
        }
        return false;
    }, [measurements, circles, selectedCircle]);
    const addMeasurementFromPoints = useCallback((startLat, startLng, endLat, endLng, name, color, lineWidth, showLabel) => {
        if (!dataSourceRef.current)
            return null;
        const measureColor = color || DEFAULT_MEASUREMENT_COLOR;
        const startPos = Cesium.Cartesian3.fromDegrees(startLng, startLat);
        const endPos = Cesium.Cartesian3.fromDegrees(endLng, endLat);
        const distance = Cesium.Cartesian3.distance(startPos, endPos);
        const bearingDeg = computeBearingDeg(startPos, endPos);
        const startEntity = createStartPointEntity(startPos, measureColor);
        const endEntity = createEndPointEntity(endPos, measureColor);
        const lineEntity = createLineEntity(startPos, endPos, measureColor, lineWidth);
        const labelEntity = createLabelEntity(startPos, endPos, distance, defaultUnit, measureColor, bearingDeg);
        if (!startEntity || !endEntity || !lineEntity || !labelEntity)
            return null;
        const effectiveShowLabel = showLabel !== null && showLabel !== void 0 ? showLabel : true;
        if (!effectiveShowLabel)
            labelEntity.show = false;
        const id = `measurement-${Date.now()}`;
        const measureName = name || `Measurement ${measurementCounterRef.current}`;
        if (!name)
            measurementCounterRef.current += 1;
        const measurement = {
            kind: 'line',
            id,
            name: measureName,
            startPoint: startPos,
            endPoint: endPos,
            startEntity,
            endEntity,
            lineEntity,
            labelEntity,
            distanceMeters: distance,
            bearingDeg,
            color: measureColor,
            displayUnit: defaultUnit,
            showLabel: effectiveShowLabel,
            projectId: projectId || undefined,
            lineWidth
        };
        setMeasurements(prev => {
            const newMap = new Map(prev);
            newMap.set(id, measurement);
            return newMap;
        });
        if (projectId) {
            const gen = ++profileFetchGenRef.current;
            fetchLineElevationProfile(projectId, startPos, endPos, readyLayers)
                .then(profile => {
                if (gen !== profileFetchGenRef.current)
                    return;
                setMeasurements(prev => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(id);
                    if (existing) {
                        newMap.set(id, { ...existing, elevationProfile: profile || null });
                    }
                    return newMap;
                });
            })
                .catch(() => { });
        }
        return id;
    }, [createStartPointEntity, createEndPointEntity, createLineEntity, createLabelEntity, defaultUnit, projectId]);
    const duplicateMeasurement = useCallback((id) => {
        const measurement = measurements.get(id);
        if (!measurement)
            return null;
        pushUndoSnapshot();
        const startCart = Cesium.Cartographic.fromCartesian(measurement.startPoint);
        const endCart = Cesium.Cartographic.fromCartesian(measurement.endPoint);
        const startLat = Cesium.Math.toDegrees(startCart.latitude);
        const startLng = Cesium.Math.toDegrees(startCart.longitude);
        const endLat = Cesium.Math.toDegrees(endCart.latitude);
        const endLng = Cesium.Math.toDegrees(endCart.longitude);
        const { eastWestMeters, northSouthMeters } = boundingExtentMeters([
            { lat: startLat, lng: startLng },
            { lat: endLat, lng: endLng },
        ]);
        const { dLat, dLng } = computeCopyOffsetLatLng(startLat, eastWestMeters, northSouthMeters);
        const newId = addMeasurementFromPoints(startLat + dLat, startLng + dLng, endLat + dLat, endLng + dLng, undefined, // name — let auto-generate
        measurement.color, measurement.lineWidth, measurement.showLabel);
        // Auto-select so the new copy's dialog opens beside it, where a click on
        // the copy would put it, and the user can drag / edit it straight away.
        if (newId) {
            const midpoint = Cesium.Cartesian3.midpoint(Cesium.Cartesian3.fromDegrees(startLng + dLng, startLat + dLat), Cesium.Cartesian3.fromDegrees(endLng + dLng, endLat + dLat), new Cesium.Cartesian3());
            selectMeasurementById(newId, viewer ? groundedWindowPosition(viewer, midpoint) : undefined);
        }
        return newId;
    }, [viewer, measurements, addMeasurementFromPoints, selectMeasurementById, pushUndoSnapshot]);
    const duplicateCircle = useCallback((id) => {
        const circle = circles.get(id);
        if (!circle)
            return null;
        pushUndoSnapshot();
        const centerCart = Cesium.Cartographic.fromCartesian(circle.center);
        const centerLat = Cesium.Math.toDegrees(centerCart.latitude);
        const centerLng = Cesium.Math.toDegrees(centerCart.longitude);
        const diameterMeters = 2 * circle.radiusMeters;
        const { dLat, dLng } = computeCopyOffsetLatLng(centerLat, diameterMeters, diameterMeters);
        const newId = addCircleFromPoints(centerLat + dLat, centerLng + dLng, circle.radiusMeters, undefined, circle.color, circle.lineWidth, circle.areaUnit, circle.outlineColor, circle.fillOpacity, circle.showLabel);
        if (newId) {
            const newCenter = Cesium.Cartesian3.fromDegrees(centerLng + dLng, centerLat + dLat);
            selectCircleById(newId, viewer ? groundedWindowPosition(viewer, newCenter) : undefined);
        }
        return newId;
    }, [viewer, circles, addCircleFromPoints, selectCircleById, pushUndoSnapshot]);
    return {
        measurements,
        pendingMeasurement,
        selectedMeasurement,
        hoveredMeasurement,
        drawingHUD,
        dragging,
        defaultUnit,
        poppedOutProfiles,
        isVisible,
        handleMapMouseDown,
        handleMapClick,
        handleMapHover,
        handleMapMouseUp,
        checkExistingMeasurementClick,
        isEntityDraggable,
        removeMeasurement,
        updateMeasurementColor,
        updateMeasurementLineWidth,
        updateMeasurementUnit,
        updateMeasurementColorBy,
        updateMeasurementName,
        updateMeasurementShowLabel,
        updateMeasurementOpacity,
        getItemOpacity,
        clearAllMeasurements,
        cancelPending,
        dismissSelection,
        selectMeasurementById,
        popOutProfile,
        closePopOutProfile,
        restoreFromConfig,
        hideAll,
        showAll,
        toggleItemVisibility,
        isItemVisible,
        addMeasurementFromPoints,
        duplicateMeasurement,
        duplicateCircle,
        measurementCount: measurements.size,
        // Circle mode
        measureMode,
        setMeasureMode,
        circles,
        selectedCircle,
        pendingCircle,
        removeCircle,
        updateCircleColor,
        updateCircleOutlineColor,
        updateCircleFillOpacity,
        updateCircleLineWidth,
        updateCircleDisplayUnit,
        updateCircleAreaUnit,
        updateCircleName,
        updateCircleShowLabel,
        dismissCircleSelection,
        selectCircleById,
        addCircleFromPoints,
        defaultAreaUnit,
    };
};
